/**
 * api/client.ts —— 全站【唯一的 fetch 出口】
 * 出处：06_二期技术方案.md §五 接口清单、§三之五 W0-4
 *
 * 为什么必须唯一：Cookie 携带、401 处理、错误码归一，
 * 每多一个 fetch 调用点就多一处会漏掉它们的地方。一处漏掉 credentials，
 * 表现是「本地好好的，上线全部 401」——这类错误排查代价极高。
 *
 * 四条纪律：
 * 1. 【业务代码不许直接调 fetch】。要新接口就在 api/ 下加一个函数。
 * 2. 【不吞业务错误】。VERSION_CONFLICT / STATUS_IRREVERSIBLE / CONFLICT 必须抛给调用方，
 *    页面要拿它渲染冲突弹窗（§五 5.5 / 5.6）。只有 401 与网络错误在这里统一处理。
 * 3. 【不做任何口径计算】。前端只显示后端算好的值（红线 4）。
 *    看到前端出现「求平均」「算天数」就是口径分叉的开始。
 * 4. 【时间字符串原样用，不 new Date()】。全站时间基准是北京时间字符串
 *    （types/common.ts 的 DateStr / DateTimeStr）。一 new Date() 就掉进浏览器本地时区。
 */
import type { ApiError, ErrCode } from '@shared-types';

/** 接口前缀。默认同源 /api，由 Nginx 分流；开发期由 vite proxy 转发。
 *  ⚠ `?.` 不是多余的：`import.meta.env` 只有 Vite 注入，在 `node --test` 里是 undefined，
 *    写成 `import.meta.env.VITE_API_BASE` 会让**任何 import 到本文件的模块都无法被单测**
 *    （页面模块几乎都会间接 import 到它）。Vite 构建时两种写法都会被正确替换。 */
const BASE = import.meta.env?.VITE_API_BASE ?? '';

/** ⚠ 只给 `api/assistant.ts` 的流式接口用——它绕开 `req()` 自己 fetch（原因见那边的文件头），
 *  不导出的话它就会把 BASE 漏掉，`VITE_API_BASE` 一配就只有 AI 助手连不上。 */
export const apiBase = (): string => BASE;

/**
 * 服务端拒绝时抛出的异常。
 * ⚠ 页面 catch 时【判 code 不判 message】——message 是给人看的，随时会改文案。
 */
export class ApiClientError extends Error {
  readonly code: ErrCode;
  readonly httpStatus: number;
  readonly conflicts: ApiError['conflicts'];
  /** 服务器内部错误（500）才有。⚠ 显示给用户是为了让他能【念给管理员听】，见 errText() */
  readonly reqId?: string;

  constructor(
    code: ErrCode,
    message: string,
    httpStatus: number,
    conflicts?: ApiError['conflicts'],
    reqId?: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.conflicts = conflicts;
    this.reqId = reqId;
  }
}

/** 网络层失败（断网、超时、Nginx 502）。与业务拒绝分开，因为它【可重试】 */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * 401 的处理钩子。由 main.ts 注入，避免 api 层反向依赖 router。
 * ⚠ 401 表示未认证或会话失效；初始密码未更换不再触发强制改密跳转。
 */
type Hooks = {
  onUnauthorized: () => void;
};
let hooks: Hooks = { onUnauthorized: () => {} };
export function setApiHooks(h: Hooks): void {
  hooks = h;
}

export interface ReqOptions {
  /** 取消信号：切页时丢弃过期请求，见 router/render.ts 的竞态处理 */
  signal?: AbortSignal;
  /**
   * 401 时不触发跳登录。
   * ⚠ 只有 boot 阶段探测 /auth/me 该传 true —— 那时"未登录"是正常结果不是异常，
   *   不传会造成「登录页 → 探测 401 → 又跳登录页」的自我循环。
   */
  quiet401?: boolean;
}

async function req<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  opts: ReqOptions = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      // ⚠ 必须带：会话是 httpOnly Cookie（T5，不用 JWT），漏了这行全站 401
      credentials: 'include',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    // AbortError 是我们自己取消的，原样抛出去让 render 层识别，不要包成 NetworkError
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new NetworkError('网络连接失败，请检查网络后重试');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // 后端异常时 Nginx 可能直接吐 HTML 错误页，这时 payload 保持 null，走下面的兜底文案
    }
  }

  if (res.ok) return payload as T;

  const err = (payload ?? {}) as Partial<ApiError>;
  const code: ErrCode = err.code ?? 'VALIDATION';
  const message = err.message ?? `请求失败（HTTP ${res.status}）`;

  if (res.status === 401 && !opts.quiet401) hooks.onUnauthorized();

  throw new ApiClientError(code, message, res.status, err.conflicts, err.reqId);
}

export const get = <T>(path: string, opts?: ReqOptions): Promise<T> => req<T>('GET', path, undefined, opts);
export const post = <T>(path: string, body?: unknown, opts?: ReqOptions): Promise<T> => req<T>('POST', path, body, opts);
export const put = <T>(path: string, body?: unknown, opts?: ReqOptions): Promise<T> => req<T>('PUT', path, body, opts);
export const del = <T>(path: string, opts?: ReqOptions): Promise<T> => req<T>('DELETE', path, undefined, opts);

/** 把任意异常翻成给用户看的一句话。错误态与 toast 共用它，保证两处文案一致
 *  ⚠ 服务器内部错误会在末尾拼上【错误编号】——那是线上排障唯一能把用户描述对到日志行的线索。
 *    不要为了"提示更干净"把它去掉；也不要给业务拒绝加编号（后端本来就不下发）。 */
export function errText(e: unknown): string {
  if (e instanceof ApiClientError) {
    return e.reqId ? `${e.message}（错误编号 ${e.reqId}）` : e.message;
  }
  if (e instanceof NetworkError) return e.message;
  if (e instanceof Error) return e.message;
  return '发生未知错误';
}

/** 这一类错误值得给「重试」按钮；业务拒绝（如版本冲突）重试没有意义 */
export function isRetryable(e: unknown): boolean {
  if (e instanceof NetworkError) return true;
  if (e instanceof ApiClientError) return e.httpStatus >= 500;
  return false;
}
